import { useCallback, useMemo } from 'react';
import { Trans } from 'react-i18next';

import { SlidingPanel } from 'core/components';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { baseCodeService, pricingGroupService } from 'parts/services';
import { QuotePartHelper } from 'quotes/helpers';
import { IAttributePartPayload, IQuotePart } from 'quotes/types';

import { columnDef } from './columns';
import { AttributePartType } from './enums';
import { Table } from './Table';

interface IProps {
  type: AttributePartType;
  isOpen: boolean;
  onClose: () => void;
  selectedPart: IQuotePart;
  onAttachParts?: (parts: IAttributePartPayload[]) => Promise<{ newRows: IQuotePart[] }>;
}

export const AttributePartDetails = ({
  type,
  isOpen,
  onClose,
  selectedPart,
  onAttachParts,
}: IProps) => {
  const title = `titleSelectPartsFor${type}`;

  const service = useMemo(() => {
    switch (type) {
      case AttributePartType.BASE_CODE:
        return baseCodeService;
      case AttributePartType.PRICING_GROUP:
        return pricingGroupService;
    }
  }, [type]);

  const listFilters = useMemo(() => {
    switch (type) {
      case AttributePartType.BASE_CODE:
        return {
          partBaseCode: selectedPart?.partArr?.number,
          manufacturerId: selectedPart?.partArr?.manufacturerId,
          excludeIds: [selectedPart?.corporatePartConfig?.id],
        };
      case AttributePartType.PRICING_GROUP:
        return {
          pricingGroup: selectedPart?.pricingGroupDiscountName,
        };
    }
  }, [type, selectedPart]);

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) => {
      const payload = {
        filters: {
          ...listFilters,
          ...filters,
        },
        ...rest,
      };

      return service.getPartList(payload);
    },
    [service, listFilters]
  );

  const columnDefs = useMemo(() => columnDef(), []);

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      title={
        <Trans
          i18nKey={title}
          values={{
            number: QuotePartHelper.getDisplayedPartNumber(selectedPart),
          }}
        />
      }
      sx={{ '& .MuiDrawer-content': { width: '100%' } }}
    >
      {isOpen && (
        <AgGridServerTableProvider
          tableId="attributePartList"
          entityType="AttributePart"
          columnDefs={columnDefs}
          getRows={getRows}
        >
          <Table onClose={onClose} onAttachParts={onAttachParts} />
        </AgGridServerTableProvider>
      )}
    </SlidingPanel>
  );
};
